import { hasProp, isObject } from "core/utils";

import type { CustomIconCodes } from "../../../components/Icon/custom/CustomIcon";
import type { GlobalRoute, Path } from "./routerTypes";

/**
 * General Menu item
 * @param titleKey key for the title
 * @param route global route
 * @param to corresponding path
 * */
export type MenuItem = Readonly<{
    titleKey: string;
    route: GlobalRoute;
    to: Path;
    permission?: string;
}>;

/**
 * Menu item with icon
 * @param titleKey key for the title
 * @param route global route
 * @param to corresponding path
 * @param menuIcon icon from antd pack
 * */
export type MenuIconItem = Readonly<{
    titleKey: string;
    route: GlobalRoute;
    to: Path;
    menuIcon: CustomIconCodes;
    permission?: string;
}>;

/**
 * Group for menu items
 * @param key selectable key
 * @param title  key for the title
 * @param subMenus sub menu items
 * @param menuIcon icon from antd pack
 * */
export type MenuItemGroup = Readonly<{
    key: string;
    title: string;
    subMenus?: ReadonlyArray<SubMenuItem>;
    unclassifiedItems?: ReadonlyArray<MenuIconItem>;
    permission?: string;
}>;

/**
 * Wrapper for menuItems that can have icon
 * @param key selectable key
 * @param icon icon from antd pack
 * @param title  key for the title
 * @param menuItems array of menu items
 * @param actionItems array of action items (trigger callbacks instead of navigation)
 * */
export type SubMenuItem = Readonly<{
    key: string;
    icon: CustomIconCodes;
    title: string;
    menuItems: ReadonlyArray<MenuItem>;
    actionItems?: ReadonlyArray<ActionMenuItem>;
    permission?: string;
}>;

/**
 * Menu item that triggers a callback action instead of navigating to a route
 * @param titleKey key for the title
 * @param action identifier for the action to perform
 * @param permission optional permission guard
 * */
export type ActionMenuItem = Readonly<{
    titleKey: string;
    action: string;
    permission?: string;
}>;
export const isMenuItemGroup = (value: unknown): value is MenuItemGroup =>
    isObject(value) && (hasProp(value, "subMenus") || hasProp(value, "unclassifiedItems"));
